// src/Dashboard/admin/sections/OverviewSection.jsx
import {
  BarChart3,
  Users,
  GraduationCap,
  Award,
  TrendingUp,
  RefreshCw,
  MessageSquare,
  Mail,
  Bell,
  AlertCircle,
  Activity,
  Shield,
  Megaphone,
} from "lucide-react";
import { DonutChart, MiniBars } from "../components/miniCharts";

/**
 * OverviewSection
 * Pure UI component (no data fetching).
 *
 * Compatible with two prop styles:
 *  - "old": loadingStats/loadingReports/loadingComms + onRefresh
 *  - "new" (your AdminDashboard): statsLoading/reportsLoading/commsLoading + onReloadReports
 */
export default function OverviewSection({
  // data
  stats, // optional
  reportsSummary, // optional

  // loading (old names)
  loadingStats = false,
  loadingReports = false,
  loadingComms = false,

  // loading (new names - from AdminDashboard)
  statsLoading = false,
  reportsLoading = false,
  commsLoading = false,

  // errors (optional)
  error = "",
  reportsError = "",

  // optional “status” blocks
  commsProviders, // { sms:{ok,...}, email:{ok,...} } or similar
  remindersSnapshot, // { queued, sent, failed } or similar

  // actions
  onRefresh, // old name
  onReloadReports, // new name
  setActiveSection, // (id)=>void
}) {
  // Resolve loading flags in a backwards-compatible way
  const L_STATS = Boolean(statsLoading || loadingStats);
  const L_REPORTS = Boolean(reportsLoading || loadingReports);
  const L_COMMS = Boolean(commsLoading || loadingComms);

  const busy = Boolean(L_STATS || L_REPORTS || L_COMMS);

  // Resolve refresh handler
  const refreshFn = onRefresh || onReloadReports || null;

  // Resolve error
  const bannerError = String(reportsError || error || "").trim();

  const s = normalizeOverview(stats, reportsSummary);

  return (
    <div className="space-y-6 min-w-0">
      {/* Section header */}
      <div className="flex items-center justify-between gap-3 min-w-0">
        <div className="min-w-0">
          <h1 className="text-lg sm:text-xl font-semibold truncate">Overview</h1>
          <p className="text-xs text-slate-500 truncate">
            Key stats and health for the current scope.
          </p>
          {busy && (
            <div className="mt-2 text-xs text-slate-500 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" />
              Updating overview…
            </div>
          )}
        </div>

        {refreshFn ? (
          <button
            type="button"
            onClick={refreshFn}
            className="shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 hover:bg-slate-50 dark:hover:bg-slate-900 text-sm"
          >
            <RefreshCw size={16} className={busy ? "animate-spin" : ""} />
            Refresh
          </button>
        ) : null}
      </div>

      {/* Error banner */}
      {bannerError ? (
        <div className="p-3 rounded-xl border border-amber-200 bg-amber-50 text-amber-800 text-sm dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-100 flex items-center gap-2">
          <AlertCircle size={16} className="shrink-0" />
          <span className="truncate">{bannerError}</span>
        </div>
      ) : null}

      {/* KPI grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 min-w-0">
        <StatCard
          icon={Users}
          title="Learners"
          value={L_STATS ? "—" : fmtInt(s.learnersTotal)}
          subtitle={s.learnersActive != null ? `${fmtInt(s.learnersActive)} active` : "Total in scope"}
        />
        <StatCard
          icon={GraduationCap}
          title="Instructors"
          value={L_STATS ? "—" : fmtInt(s.instructors)}
          subtitle="Active accounts"
        />
        <StatCard
          icon={Award}
          title="Certificates"
          value={L_STATS ? "—" : fmtInt(s.certificates)}
          subtitle="Issued in scope"
        />
        <StatCard
          icon={TrendingUp}
          title="Completions"
          value={L_STATS ? "—" : fmtInt(s.completions)}
          subtitle="Course completions"
        />
      </div>

      {/* Analytics block */}
      <div className="grid lg:grid-cols-3 gap-4 min-w-0">
        <div className="lg:col-span-2 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm min-w-0">
          <div className="flex items-center gap-3 mb-4 min-w-0">
            <div className="p-2 rounded-xl bg-orange-50 dark:bg-orange-500/10 shrink-0">
              <BarChart3 size={20} className="text-orange-600 dark:text-orange-300" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold truncate">Analytics</h2>
              <p className="text-xs text-slate-500 truncate">
                Key rates in the current scope (auto-adapts to your data).
              </p>
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            <div className="rounded-2xl border border-slate-200 dark:border-slate-800 p-4 bg-slate-50 dark:bg-slate-950 min-w-0">
              <DonutChart
                value={L_REPORTS ? 0 : s.completionRatePct}
                label="Completion rate"
                sublabel={
                  s.enrolled != null
                    ? `${fmtInt(s.completed)} / ${fmtInt(s.enrolled)} completed`
                    : " "
                }
                tone="orange"
              />
            </div>

            <div className="rounded-2xl border border-slate-200 dark:border-slate-800 p-4 bg-slate-50 dark:bg-slate-950 min-w-0">
              <DonutChart
                value={L_REPORTS ? 0 : s.passRatePct}
                label="Pass rate"
                sublabel={
                  s.attempts != null ? `${fmtInt(s.passed)} / ${fmtInt(s.attempts)} passed` : " "
                }
                tone="green"
              />
            </div>

            <div className="rounded-2xl border border-slate-200 dark:border-slate-800 p-4 bg-slate-50 dark:bg-slate-950 min-w-0">
              <DonutChart
                value={L_STATS ? 0 : s.activeRatePct}
                label="Active learners"
                sublabel={
                  s.learnersActive != null && s.learnersTotal != null
                    ? `${fmtInt(s.learnersActive)} / ${fmtInt(s.learnersTotal)} active`
                    : " "
                }
                tone="slate"
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            <MiniStatusCard
              title="Avg score"
              value={L_REPORTS ? "—" : s.avgScorePct != null ? `${fmtNum(s.avgScorePct)}%` : "—"}
              subtitle="Average best score"
              icon={TrendingUp}
            />
            <MiniStatusCard
              title="Attempts"
              value={L_REPORTS ? "—" : s.attempts != null ? fmtInt(s.attempts) : "—"}
              subtitle="Total attempts"
              icon={Activity}
            />
          </div>
        </div>

        {/* Right rail */}
        <div className="space-y-4 min-w-0">
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm min-w-0">
            <h2 className="text-lg font-semibold">Quick actions</h2>
            <p className="text-xs text-slate-500 mt-1">Jump to common admin tasks.</p>

            <div className="mt-4 space-y-2">
              <QuickNav label="View learners" icon={Users} onClick={() => setActiveSection?.("people-learners")} />
              <QuickNav
                label="Manage instructors"
                icon={GraduationCap}
                onClick={() => setActiveSection?.("people-instructors")}
              />
              <QuickNav label="Manage admins" icon={Shield} onClick={() => setActiveSection?.("people-admins")} />
              <QuickNav label="Post announcement" icon={Megaphone} onClick={() => setActiveSection?.("announcements")} />
              <QuickNav label="Send SMS" icon={MessageSquare} onClick={() => setActiveSection?.("sms")} />
              <QuickNav label="Send Email" icon={Mail} onClick={() => setActiveSection?.("email")} />
              <QuickNav label="Open reports" icon={BarChart3} onClick={() => setActiveSection?.("reports")} />
              <QuickNav label="View reminders" icon={Bell} onClick={() => setActiveSection?.("reminders")} />
            </div>
          </div>

          <MiniBars title="Top courses" items={s.topCourses} suffix="" />
        </div>
      </div>

      {/* Communication + reminders snapshot */}
      <div className="grid lg:grid-cols-2 gap-4 min-w-0">
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm min-w-0">
          <div className="flex items-center gap-3 mb-4 min-w-0">
            <div className="p-2 rounded-xl bg-slate-50 dark:bg-slate-800/60 shrink-0">
              <MessageSquare size={18} className="text-slate-700 dark:text-slate-200" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold truncate">Communication</h2>
              <p className="text-xs text-slate-500 truncate">Provider health snapshot.</p>
            </div>
          </div>

          {!commsProviders ? (
            <EmptyNote icon={AlertCircle} text="Comms status not connected here yet." />
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              <ProviderTile
                icon={MessageSquare}
                title="SMS"
                ok={Boolean(commsProviders?.sms?.ok ?? commsProviders?.smsOk)}
                subtitle={commsProviders?.sms?.name || commsProviders?.sms?.provider || "Provider"}
              />
              <ProviderTile
                icon={Mail}
                title="Email"
                ok={Boolean(commsProviders?.email?.ok ?? commsProviders?.emailOk)}
                subtitle={commsProviders?.email?.name || commsProviders?.email?.provider || "Provider"}
              />
            </div>
          )}
        </div>

        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm min-w-0">
          <div className="flex items-center gap-3 mb-4 min-w-0">
            <div className="p-2 rounded-xl bg-slate-50 dark:bg-slate-800/60 shrink-0">
              <Bell size={18} className="text-slate-700 dark:text-slate-200" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold truncate">Reminders</h2>
              <p className="text-xs text-slate-500 truncate">Queue summary (if available).</p>
            </div>
          </div>

          {!remindersSnapshot ? (
            <EmptyNote icon={AlertCircle} text="Reminders snapshot not connected here yet." />
          ) : (
            <div className="space-y-2">
              <RowStat label="Queued" value={fmtInt(remindersSnapshot.queued)} />
              <RowStat label="Sent" value={fmtInt(remindersSnapshot.sent)} />
              <RowStat label="Failed" value={fmtInt(remindersSnapshot.failed)} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* --------------------------------- adapter -------------------------------- */

function normalizeOverview(stats, reportsSummary) {
  // Backend shapes:
  // - stats endpoint: { ok:true, data:{ total, active, graduated, avgProgress } }
  // - reports summary: { meta, totals:{...}, grouped:[...], courses:[...] }

  const statsData = stats?.data && typeof stats.data === "object" ? stats.data : (stats || {});
  const rep = reportsSummary && typeof reportsSummary === "object" ? reportsSummary : {};
  const repTotals = rep?.totals && typeof rep.totals === "object" ? rep.totals : {};

  // Merge (reports totals should win over stats if present)
  const src = { ...statsData, ...repTotals, ...rep };

  // learners
  const learnersTotal = pickNum(src, [
    "learners",
    "totalLearners",
    "total_learners",
    "users",
    "totalUsers",
    "total", // ✅ from /admin/stats + reports.totals.total
  ]);

  const learnersActive = pickNum(src, [
    "activeLearners",
    "active_learners",
    "learners_active",
    "active", // ✅ from /admin/stats + reports.totals.active
  ]);

  const learnersInactive = pickNum(src, [
    "inactiveLearners",
    "inactive_learners",
    "learners_inactive",
    "inactive", // ✅ from reports.totals.inactive
  ]);

  // ✅ certificates / graduated
  // In reports controller, "graduated" is driven by certificates table.
  // So showing it as "Certificates" in overview is correct enough for now.
  const certificates = pickNum(src, [
    "certificates",
    "totalCertificates",
    "total_certificates",
    "graduated", // ✅ from /admin/stats + reports.totals.graduated
  ]);

  // completions: if your RPC doesn't provide a "completions" total, we infer from courses
  // (sum of learners_completed across courses)
  let inferredCompletions = null;

  // attempts: sum total_attempts from courses (if available)
  let inferredAttempts = null;

  // enrolled/completed for completion rate
  let inferredEnrolled = null;
  let inferredCompleted = null;

  // pass rate: your courses rows have pass_rate_pct (already a percent).
  // We'll compute a weighted average by attempts if possible, else simple average.
  let inferredPassRatePct = null;

  const coursesArr = Array.isArray(rep?.courses) ? rep.courses : Array.isArray(src?.courses) ? src.courses : [];

  if (Array.isArray(coursesArr) && coursesArr.length) {
    let sumEnrolled = 0;
    let sumCompleted = 0;
    let sumAttempts = 0;

    let passNum = 0;
    let passDen = 0;

    for (const c of coursesArr) {
      const enrolled = Number(c?.enrolled ?? 0);
      const completed = Number(c?.learners_completed ?? c?.completed ?? 0);
      const attempts = Number(c?.total_attempts ?? c?.attempts ?? 0);

      if (Number.isFinite(enrolled)) sumEnrolled += enrolled;
      if (Number.isFinite(completed)) sumCompleted += completed;
      if (Number.isFinite(attempts)) sumAttempts += attempts;

      const pr = Number(c?.pass_rate_pct ?? c?.passRatePct);
      if (Number.isFinite(pr)) {
        // weight by attempts if available, otherwise weight by enrolled, otherwise 1
        const w = Number.isFinite(attempts) && attempts > 0 ? attempts : (Number.isFinite(enrolled) && enrolled > 0 ? enrolled : 1);
        passNum += pr * w;
        passDen += w;
      }
    }

    inferredEnrolled = sumEnrolled;
    inferredCompleted = sumCompleted;
    inferredCompletions = sumCompleted; // practical definition for overview
    inferredAttempts = sumAttempts;

    if (passDen > 0) inferredPassRatePct = Math.round(passNum / passDen);
  }

  // reports-style numbers (direct, if ever provided)
  const enrolled = pickNum(src, ["enrolled", "enrolled_count", "total_enrolled"], inferredEnrolled);
  const completed = pickNum(src, ["completed", "completed_count", "total_completed"], inferredCompleted);
  const attempts = pickNum(src, ["attempts", "totalAttempts", "total_attempts"], inferredAttempts);

  // pass rate
  const passRatePct = clamp0_100(
    pickNum(src, ["passRatePct", "pass_rate_pct"], inferredPassRatePct != null ? inferredPassRatePct : 0)
  );

  // completion rate
  const completionRatePct = clamp0_100(
    pickNum(src, ["completionRatePct", "completion_rate_pct"], pct(completed, enrolled))
  );

  // avg score: only if your RPC exposes it later; keep fallback null
  const avgScorePct = clamp0_100(
    pickNum(src, ["avgScorePct", "avg_score_pct", "avgBestPercent", "avg_best_percent"], null)
  );

  // active rate
  const activeRatePct =
    learnersTotal > 0 && learnersActive != null
      ? clamp0_100(Math.round((numOr0(learnersActive) / numOr0(learnersTotal)) * 100))
      : 0;

  // top courses: use reports.courses by default (enrolled)
  const topCourses = Array.isArray(coursesArr)
    ? coursesArr
      .map((c) => ({
        label: c?.label || c?.name || c?.course_slug || c?.slug || "Course",
        value: Number(c?.enrolled ?? c?.enrollments ?? c?.count ?? 0),
      }))
      .filter((x) => x.label && Number.isFinite(x.value))
      .sort((m, n) => (n.value || 0) - (m.value || 0))
      .slice(0, 6)
    : [];

  // instructors/completions: instructors not provided by these endpoints → 0 (until you wire it)
  const instructors = pickNum(src, ["instructors", "totalInstructors", "total_instructors"], 0);

  const completions = pickNum(src, ["completions", "totalCompletions", "total_completions"], inferredCompletions);

  return {
    learnersTotal: numOr0(learnersTotal),
    learnersActive: learnersActive == null ? null : numOr0(learnersActive),
    learnersInactive: learnersInactive == null ? null : numOr0(learnersInactive),

    instructors: numOr0(instructors),

    // show graduated as certificates issued (current best truth from your backend)
    certificates: numOr0(certificates),

    completions: completions == null ? 0 : numOr0(completions),

    enrolled: enrolled == null ? null : numOr0(enrolled),
    completed: completed == null ? 0 : numOr0(completed),

    attempts: attempts == null ? null : numOr0(attempts),
    passed: 0, // not available from your current RPC shape (fine)

    completionRatePct,
    passRatePct,
    avgScorePct: avgScorePct || null,
    activeRatePct,

    topCourses,
  };
}


function pickNum(obj, keys, fallback = null) {
  for (const k of keys) {
    const v = obj?.[k];
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return fallback;
}

function numOr0(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function pct(a, b) {
  const A = numOr0(a);
  const B = numOr0(b);
  if (B <= 0) return 0;
  return Math.round((A / B) * 100);
}

function clamp0_100(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, Math.round(n)));
}

function fmtInt(v) {
  try {
    return new Intl.NumberFormat().format(Number(v || 0));
  } catch {
    return String(v ?? 0);
  }
}

function fmtNum(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "0";
  return String(Math.round(n));
}

/* --------------------------------- UI bits -------------------------------- */

function StatCard({ icon: Icon, title, value, subtitle }) {
  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 p-5 min-w-0">
      <div className="flex items-center gap-3 min-w-0">
        <div className="p-2 rounded-xl bg-orange-50 dark:bg-orange-500/10 shrink-0">
          <Icon size={20} className="text-orange-600 dark:text-orange-300" />
        </div>
        <div className="min-w-0">
          <div className="text-xs text-slate-500 truncate">{title}</div>
          <div className="text-2xl font-semibold truncate">{value}</div>
          {subtitle ? <div className="text-xs text-slate-400 truncate">{subtitle}</div> : null}
        </div>
      </div>
    </div>
  );
}

function MiniStatusCard({ title, value, subtitle, icon: Icon }) {
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-800 p-4 bg-white dark:bg-slate-900 min-w-0">
      <div className="flex items-center gap-3 min-w-0">
        <div className="p-2 rounded-xl bg-slate-50 dark:bg-slate-800/60 shrink-0">
          <Icon size={18} className="text-slate-700 dark:text-slate-200" />
        </div>
        <div className="min-w-0">
          <div className="text-xs text-slate-500 truncate">{title}</div>
          <div className="text-base font-semibold truncate">{String(value)}</div>
          <div className="text-xs text-slate-400 truncate">{subtitle}</div>
        </div>
      </div>
    </div>
  );
}

function RowStat({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm min-w-0">
      <span className="text-slate-600 dark:text-slate-300 truncate">{label}</span>
      <span className="font-semibold shrink-0">{value}</span>
    </div>
  );
}

function ProviderTile({ icon: Icon, title, ok, subtitle }) {
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-800 p-4 bg-white dark:bg-slate-900 min-w-0">
      <div className="flex items-center gap-3 min-w-0">
        <div className="p-2 rounded-xl bg-slate-50 dark:bg-slate-800/60 shrink-0">
          <Icon size={18} className="text-slate-700 dark:text-slate-200" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm font-semibold truncate">{title}</div>
            <span
              className={`text-[11px] font-semibold px-2 py-1 rounded-full border shrink-0 ${ok
                  ? "bg-green-50 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-200 dark:border-green-800"
                  : "bg-amber-50 text-amber-900 border-amber-200 dark:bg-amber-900/20 dark:text-amber-100 dark:border-amber-800"
                }`}
            >
              {ok ? "OK" : "Check"}
            </span>
          </div>
          <div className="text-xs text-slate-500 truncate">{subtitle}</div>
        </div>
      </div>
    </div>
  );
}

function QuickNav({ label, onClick, icon: Icon }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/60 text-sm flex items-center gap-2"
    >
      {Icon ? <Icon size={16} className="text-slate-500" /> : null}
      <span>{label}</span>
    </button>
  );
}

function EmptyNote({ icon: Icon, text }) {
  return (
    <div className="flex items-start gap-2 text-sm text-slate-500">
      <Icon size={16} className="mt-0.5 shrink-0" />
      <span>{text}</span>
    </div>
  );
}
